// useStore.js - Zustand State Management (Refactored with Slices)

import { create } from 'zustand';
import { temporal } from 'zundo';
import { SignalChainCalculator } from '../utils/signalChainCalculator';
import { api } from '../utils/api';
import { COMPONENT_LIBRARY } from '../data/componentLibrary';
import { injectDataSourceParameters } from '../data/dataSourceParameters';
import { interpolateParameter, getClosestVoltage } from '../utils/freqDataInterpolator';
import { interpolateS21 } from '../utils/s2pInterpolator';
import { scoreAndRankRecommendations, extractGenericParams } from '../utils/componentMatcher';
import { debugLog } from '../utils/debugLog';

// Import slices
import { createAuthSlice } from './slices/authSlice';
import { createProjectSlice } from './slices/projectSlice';
import { createUISlice } from './slices/uiSlice';
import { createTutorialSlice } from './slices/tutorialSlice';
import { createFrequencySlice } from './slices/frequencySlice';

// API base URL - uses environment variable or defaults to localhost for development
const API_BASE_URL = '';

// Create store with temporal middleware for undo/redo
const useStore = create(
  temporal(
    (set, get) => ({
  // ============================================================================
  // SLICES - Modular state management
  // ============================================================================
  ...createAuthSlice(set),
  ...createProjectSlice(set),
  ...createUISlice(set),
  ...createTutorialSlice(set, get),
  ...createFrequencySlice(set, get),

  // ============================================================================
  // COMPONENT LIBRARY
  // ============================================================================
  componentLibrary: {},
  catalogComponents: [],
  isLoadingComponents: false,
  componentLoadError: null,

  loadComponentLibrary: async () => {
    set({ isLoadingComponents: true, componentLoadError: null });
    try {
      const response = await api.getComponents();
      const merged = { ...COMPONENT_LIBRARY };

      if (response.catalog && Array.isArray(response.catalog)) {
        response.catalog.forEach(comp => {
          let baseType = comp.baseType;
          if (!baseType) {
            throw new Error(`Component ${comp.partNumber || comp.type} is missing baseType/subcategory`);
          }

          const baseComponentDef = COMPONENT_LIBRARY[baseType] || COMPONENT_LIBRARY['generic'];

          // Merge database parameters with base component parameters
          // Inherit displayOnCard and other metadata from base type
          const mergedParams = {};
          const dbParams = comp.parameters || {};
          const baseParams = baseComponentDef.parameters || {};

          // Start with all keys from both sources
          const allKeys = new Set([...Object.keys(dbParams), ...Object.keys(baseParams)]);

          for (const key of allKeys) {
            if (dbParams[key]) {
              // Database has this parameter - use it but inherit displayOnCard from base
              mergedParams[key] = {
                ...dbParams[key],
                displayOnCard: baseParams[key]?.displayOnCard ?? dbParams[key]?.displayOnCard ?? false
              };
            } else if (baseParams[key]) {
              // Only in base - include it
              mergedParams[key] = { ...baseParams[key] };
            }
          }

          const catalogComponent = {
            type: comp.type,
            displayName: comp.displayName,
            category: comp.category,
            subcategory: comp.subcategory || baseType,
            manufacturer: comp.manufacturer,
            partNumber: comp.partNumber,
            isCatalog: true,
            isFavorite: comp.isFavorite || false,
            baseType: baseType,
            parameters: mergedParams,
            inputs: baseComponentDef.inputs || ['rfIn'],
            outputs: baseComponentDef.outputs || ['rfOut'],
            id: comp.id,
            hasFreqData: comp.hasFreqData || false,
            hasS2P: comp.hasS2P || false
          };

          merged[comp.type] = injectDataSourceParameters(catalogComponent);
        });
      }

      set({
        componentLibrary: merged,
        catalogComponents: response.catalog || [],
        isLoadingComponents: false
      });
    } catch (error) {
      console.error('Failed to load component library:', error);
      set({
        componentLoadError: error.message || 'Failed to load components',
        isLoadingComponents: false,
        componentLibrary: COMPONENT_LIBRARY,
        catalogComponents: []
      });
    }
  },

  createCatalogComponent: async (componentData) => {
    try {
      const newComponent = await api.createComponent(componentData);
      await get().loadComponentLibrary();
      return newComponent;
    } catch (error) {
      console.error('Failed to create catalog component:', error);
      throw error;
    }
  },

  updateCatalogComponent: async (id, componentData) => {
    try {
      await api.updateComponent(id, componentData);
      await get().loadComponentLibrary();
    } catch (error) {
      console.error('Failed to update catalog component:', error);
      throw error;
    }
  },

  deleteCatalogComponent: async (id) => {
    try {
      await api.deleteComponent(id);
      await get().loadComponentLibrary();
    } catch (error) {
      console.error('Failed to delete catalog component:', error);
      throw error;
    }
  },

  toggleFavorite: async (id, isFavorite) => {
    try {
      await api.toggleFavorite(id, isFavorite);
      await get().loadComponentLibrary();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  },

  // ============================================================================
  // REACT FLOW STATE (Canvas)
  // ============================================================================
  nodes: [],
  edges: [],
  hasUnsavedChanges: false,

  // Helper to initialize catalog component data (freq data, S2P, data sources)
  initializeCatalogNode: async (node) => {
    if (!node.data?.isCatalog) return node;

    const { loadFrequencyData, loadS2PData, getAvailableVoltages, nodes } = get();

    // Load frequency data and initialize data sources
    if (node.data.hasFreqData) {
      try {
        const inputSource = nodes.find(n => n.type === 'inputSource');
        const currentFrequency = inputSource?.data?.parameters?.frequency?.value || null;

        // First fetch available voltages for this component
        const availableVoltages = await getAvailableVoltages(node.data.partNumber);

        // Store available voltages in node data for VoltageSelector to use
        if (availableVoltages && availableVoltages.length > 0) {
          node.data.availableVoltages = availableVoltages;

          // Pick the correct voltage using same logic as VoltageSelector
          const supplyVoltage = node.data.parameters?.supplyVoltage?.value || 5.0;
          const voltage = getClosestVoltage(supplyVoltage, availableVoltages);

          const freqData = await loadFrequencyData(node.data.partNumber, voltage);

          if (freqData && freqData.length > 0) {
            const { initializeDataSourceValues } = require('../data/dataSourceParameters');
            node.data.parameters = initializeDataSourceValues(
              node.data,
              node.data.parameters,
              currentFrequency,
              freqData
            );
            if (node.data.parameters.freqDbData) {
              node.data.parameters.freqDbData.value = freqData;
            }
          }
        }
      } catch (error) {
        console.error('[initializeCatalogNode] Failed to load frequency data:', error);
      }
    }

    // Load S2P data
    if (node.data.hasS2P) {
      try {
        const s2pData = await loadS2PData(node.data.id);
        if (s2pData && node.data.parameters.s2pData) {
          node.data.parameters.s2pData.value = s2pData;
        }
      } catch (error) {
        console.error('[initializeCatalogNode] Failed to load S2P data:', error);
      }
    }

    return node;
  },

  setNodes: (nodesOrUpdater) => {
    const beforeNodes = get().nodes;
    if (typeof nodesOrUpdater === 'function') {
      set((state) => ({ nodes: nodesOrUpdater(state.nodes), hasUnsavedChanges: true }));
    } else {
      set({ nodes: nodesOrUpdater, hasUnsavedChanges: true });
    }
    const afterNodes = get().nodes;
    // Log state change for debugging zundo
    debugLog('SET_NODES', {
      beforeCount: beforeNodes?.length,
      afterCount: afterNodes?.length,
      pastStatesAfter: useStore.temporal?.getState()?.pastStates?.length
    });
    Promise.resolve().then(() => get().recalculateChain());
  },

  setNodesWithoutModifying: (nodesOrUpdater) => {
    if (typeof nodesOrUpdater === 'function') {
      set((state) => ({ nodes: nodesOrUpdater(state.nodes) }));
    } else {
      set({ nodes: nodesOrUpdater });
    }
    Promise.resolve().then(() => get().recalculateChain());
  },

  setEdges: (edges) => {
    set({ edges, hasUnsavedChanges: true });
    Promise.resolve().then(() => get().recalculateChain());
  },

  setEdgesWithoutModifying: (edges) => {
    set({ edges });
    Promise.resolve().then(() => get().recalculateChain());
  },

  addNode: async (node) => {
    // Initialize catalog components (load freq data, S2P, set data sources)
    if (node.data?.isCatalog) {
      const { initializeCatalogNode } = get();
      node = await initializeCatalogNode(node);
    }

    set((state) => ({ nodes: [...state.nodes, node], hasUnsavedChanges: true }));
    Promise.resolve().then(() => get().recalculateChain());
  },

  updateNode: (nodeId, updates) => {
    set((state) => ({
      nodes: state.nodes.map(node =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
      hasUnsavedChanges: true
    }));
    Promise.resolve().then(() => get().recalculateChain());
  },

  deleteNode: (nodeId) => {
    set((state) => ({
      nodes: state.nodes.filter(node => node.id !== nodeId),
      edges: state.edges.filter(edge =>
        edge.source !== nodeId && edge.target !== nodeId
      )
    }));
    Promise.resolve().then(() => get().recalculateChain());
  },

  updateNodeData: (nodeId, dataUpdates) => {
    // Capture state before change for debugging
    const inputSourceBefore = get().nodes.find(n => n.type === 'inputSource');
    const powerBefore = inputSourceBefore?.data?.parameters?.power?.value;

    set((state) => ({
      nodes: state.nodes.map(node =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...dataUpdates } }
          : node
      ),
      hasUnsavedChanges: true
    }));

    // Log for debugging
    const inputSourceAfter = get().nodes.find(n => n.type === 'inputSource');
    const powerAfter = inputSourceAfter?.data?.parameters?.power?.value;
    const temporal = useStore.temporal?.getState();
    const lastPastState = temporal?.pastStates?.[temporal.pastStates.length - 1];
    const lastPastInputSource = lastPastState?.nodes?.find(n => n.type === 'inputSource');
    const powerInLastPastState = lastPastInputSource?.data?.parameters?.power?.value;

    debugLog('UPDATE_NODE_DATA', {
      nodeId,
      powerBefore,
      powerAfter,
      powerInLastPastState,
      pastStatesLength: temporal?.pastStates?.length
    });

    Promise.resolve().then(() => get().recalculateChain());
  },

  // ============================================================================
  // CALCULATION RESULTS
  // ============================================================================
  calculationResults: {},

  recalculateChain: () => {
    const { nodes, edges, frequencyMode, frequencySingle, runFrequencySweep } = get();

    if (nodes.length === 0) {
      set({ calculationResults: {} });
      return;
    }

    try {
      const calculator = new SignalChainCalculator(nodes, edges, {
        currentFrequency: frequencyMode === 'single' ? frequencySingle : null
      });
      const results = calculator.calculateChain();
      set({ calculationResults: results });

      // Also run sweep when in sweep mode
      if (frequencyMode === 'sweep') {
        runFrequencySweep();
      }
    } catch (error) {
      console.error('Calculation error:', error);
      set({ calculationResults: {} });
    }
  },

  // ============================================================================
  // FREQUENCY DATA & S2P MANAGEMENT
  // ============================================================================
  freqDataCache: {},
  s2pDataCache: {},
  freqDataInterpolationCache: {},
  s2pInterpolationCache: {},

  loadFrequencyData: async (partNumber, voltage) => {
    try {
      const token = localStorage.getItem('gchain_token');
      if (!token) {
        console.error('[FREQ DATA API] No authentication token found.');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/frequency-data/${partNumber}/${voltage}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Failed to fetch frequency data'}`);
      }

      const result = await response.json();

      set(state => ({
        freqDataCache: {
          ...state.freqDataCache,
          [partNumber]: {
            ...state.freqDataCache[partNumber],
            [voltage]: result.data
          }
        }
      }));

      return result.data;
    } catch (error) {
      console.error('[FREQ DATA API ERROR]:', error);
      return null;
    }
  },

  getAvailableVoltages: async (partNumber) => {
    try {
      const token = localStorage.getItem('gchain_token');
      if (!token) return [];

      const response = await fetch(`${API_BASE_URL}/api/frequency-data/voltages/${partNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Failed to fetch voltages'}`);
      }

      const result = await response.json();
      return result.voltages || [];
    } catch (error) {
      console.error('[VOLTAGE API ERROR]:', error);
      return [];
    }
  },

  loadS2PData: async (componentLibraryId, options = {}) => {
    // Check cache first
    const { s2pDataCache } = get();
    if (s2pDataCache[componentLibraryId]) {
      return s2pDataCache[componentLibraryId];
    }

    try {
      const token = localStorage.getItem('gchain_token');
      if (!token) return null;

      const queryParams = options.full ? '?full=true' : '';
      const response = await fetch(`${API_BASE_URL}/api/components/${componentLibraryId}/s2p${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP ${response.status}: ${errorData.error || 'Failed to fetch S2P data'}`);
      }

      const result = await response.json();

      set(state => ({
        s2pDataCache: {
          ...state.s2pDataCache,
          [componentLibraryId]: result.data
        }
      }));

      return result.data;
    } catch (error) {
      console.error('[S2P API ERROR]:', error);
      return null;
    }
  },

  handleVoltageChange: async (nodeId, newVoltage, voltageMode = 'manual') => {
    const { nodes, loadFrequencyData, recalculateChain, markAsModified } = get();
    const node = nodes.find(n => n.id === nodeId);

    if (!node || !node.data.partNumber) return;

    const roundedVoltage = Math.round(newVoltage * 100) / 100;
    const freqData = await loadFrequencyData(node.data.partNumber, roundedVoltage);

    if (freqData) {
      set(state => ({
        nodes: state.nodes.map(n =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...n.data,
                  parameters: {
                    ...n.data.parameters,
                    selectedVoltage: { ...n.data.parameters.selectedVoltage, value: roundedVoltage },
                    supplyVoltageMode: { ...n.data.parameters.supplyVoltageMode, value: voltageMode },
                    freqDbData: { ...n.data.parameters.freqDbData, value: freqData }
                  }
                }
              }
            : n
        ),
        freqDataInterpolationCache: {
          ...state.freqDataInterpolationCache,
          [nodeId]: {}
        }
      }));

      recalculateChain();
      markAsModified();
    }
  },

  handleDataSourceChange: async (nodeId, paramName, newSource) => {
    const { nodes, recalculateChain, markAsModified, frequencySingle } = get();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const params = node.data.parameters;
    const keyMap = {
      gain: { dataSource: 'gainDataSource', manualValue: 'gainManualValue' },
      noiseFigure: { dataSource: 'nfDataSource', manualValue: 'nfManualValue' },
      p1db: { dataSource: 'p1dbDataSource', manualValue: 'p1dbManualValue' },
      oip3: { dataSource: 'oip3DataSource', manualValue: 'oip3ManualValue' }
    };
    const dataSourceKey = keyMap[paramName]?.dataSource || `${paramName}DataSource`;
    const manualValueKey = keyMap[paramName]?.manualValue || `${paramName}ManualValue`;
    const currentSource = params[dataSourceKey]?.value || 'manual';
    let updates = {};

    // Get current frequency from input source
    const inputSource = nodes.find(n => n.type === 'inputSource');
    const currentFrequency = inputSource?.data?.parameters?.frequency?.value || frequencySingle;

    if (currentSource === 'manual' && newSource !== 'manual') {
      updates[manualValueKey] = { ...params[manualValueKey], value: params[paramName]?.value };
    }

    if (newSource === 'manual') {
      const manualValue = params[manualValueKey]?.value;
      if (manualValue !== null && manualValue !== undefined) {
        updates[paramName] = { ...params[paramName], value: manualValue };
      }
    } else if (newSource === 'database') {
      const freqData = params.freqDbData?.value;
      if (freqData && Array.isArray(freqData) && freqData.length > 0 && currentFrequency) {
        const paramMap = { gain: 'gain_db', noiseFigure: 'nf_db', p1db: 'p1db_dbm', oip3: 'oip3_dbm' };
        const dbColumnName = paramMap[paramName];
        if (dbColumnName) {
          const result = interpolateParameter(freqData, dbColumnName, currentFrequency);
          if (result && result.value !== null && result.value !== undefined) {
            updates[paramName] = { ...params[paramName], value: parseFloat(result.value.toFixed(1)) };
          }
        }
      }
    } else if (newSource === 's2p') {
      let s2pData = params.s2pData?.value;

      // Load S2P data on-demand if not already present
      if (!s2pData && node.data.isCatalog) {
        const { componentLibrary, loadS2PData } = get();
        const componentLibraryId = componentLibrary[node.type]?.id;
        if (componentLibraryId) {
          s2pData = await loadS2PData(componentLibraryId);
          if (s2pData) {
            updates.s2pData = { ...params.s2pData, value: s2pData };
            updates.hasS2PData = { ...params.hasS2PData, value: true };
          }
        }
      }

      if (s2pData && s2pData.frequencies && currentFrequency && paramName === 'gain') {
        const result = interpolateS21(s2pData, currentFrequency);
        if (result && result.gain !== null && result.gain !== undefined) {
          updates[paramName] = { ...params[paramName], value: parseFloat(result.gain.toFixed(1)) };
        }
      }
    }

    updates[dataSourceKey] = { ...params[dataSourceKey], value: newSource };

    set(state => ({
      nodes: state.nodes.map(n =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, parameters: { ...n.data.parameters, ...updates } } }
          : n
      ),
      freqDataInterpolationCache: { ...state.freqDataInterpolationCache, [nodeId]: {} },
      s2pInterpolationCache: { ...state.s2pInterpolationCache, [nodeId]: {} }
    }));

    recalculateChain();
    markAsModified();
  },

  preCalculateSweepInterpolations: (startFreq, stopFreq, numPoints = 101) => {
    const { nodes } = get();
    const step = (stopFreq - startFreq) / (numPoints - 1);
    const frequencies = [];

    for (let i = 0; i < numPoints; i++) {
      frequencies.push(startFreq + i * step);
    }

    const freqDataCache = {};
    const s2pCache = {};

    const { preCalculateFreqDataInterpolations } = require('../utils/freqDataInterpolator');
    const { preCalculateS2PInterpolations } = require('../utils/s2pInterpolator');

    nodes.forEach(node => {
      const params = node.data?.parameters || {};

      if (params.freqDbData?.value) {
        freqDataCache[node.id] = preCalculateFreqDataInterpolations(params.freqDbData.value, frequencies);
      }

      if (params.s2pData?.value) {
        s2pCache[node.id] = preCalculateS2PInterpolations(params.s2pData.value, frequencies);
      }
    });

    set({
      freqDataInterpolationCache: freqDataCache,
      s2pInterpolationCache: s2pCache
    });
  },

  clearInterpolationCaches: () => {
    set({
      freqDataInterpolationCache: {},
      s2pInterpolationCache: {}
    });
  },

  // ============================================================================
  // UNSAVED CHANGES TRACKING
  // ============================================================================
  markAsSaved: () => set({ hasUnsavedChanges: false }),
  markAsModified: () => set({ hasUnsavedChanges: true }),

  // ============================================================================
  // CATALOG COMPONENT RECOMMENDATIONS
  // ============================================================================
  recommendationCache: {},
  isLoadingRecommendations: false,
  recommendationError: null,

  // Fetch recommendations for a generic component
  // Returns { recommendations: [], frequency: number } or throws error
  fetchRecommendations: async (nodeId) => {
    const { nodes, frequencyMode, frequencyStart, frequencyStop } = get();

    const node = nodes.find(n => n.id === nodeId);
    if (!node || node.data?.isCatalog) {
      return { recommendations: [], frequency: null }; // Only works for generic components
    }

    // Get the operating frequency - NO FALLBACKS
    const inputSource = nodes.find(n => n.type === 'inputSource');
    let targetFrequency;

    if (frequencyMode === 'sweep') {
      // Use center of sweep range
      targetFrequency = (frequencyStart + frequencyStop) / 2;
    } else {
      // Single mode - get frequency from store (frequencySingle is the source of truth)
      targetFrequency = get().frequencySingle;
      if (targetFrequency === undefined || targetFrequency === null) {
        throw new Error('Could not get frequency from store');
      }
    }

    // Get the component's baseType (e.g., 'lna', 'gainBlock', etc.)
    const baseType = node.type;

    // Check cache
    const cacheKey = `${baseType}_${Math.round(targetFrequency)}`;
    const cached = get().recommendationCache[cacheKey];
    if (cached && Date.now() - cached.timestamp < 60000) {
      // Cache valid for 1 minute
      const genericParams = extractGenericParams(node);
      const scored = scoreAndRankRecommendations(cached.data, genericParams, baseType);
      return { recommendations: scored, frequency: targetFrequency };
    }

    set({ isLoadingRecommendations: true, recommendationError: null });

    try {
      const token = localStorage.getItem('gchain_token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(
        `${API_BASE_URL}/api/recommendations/${baseType}?frequency=${targetFrequency}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Cache the raw API response
      set(state => ({
        recommendationCache: {
          ...state.recommendationCache,
          [cacheKey]: {
            data: result.recommendations,
            timestamp: Date.now()
          }
        },
        isLoadingRecommendations: false
      }));

      // Score and rank based on the specific node's parameters
      const genericParams = extractGenericParams(node);
      const scored = scoreAndRankRecommendations(result.recommendations, genericParams, baseType);
      return { recommendations: scored, frequency: targetFrequency };

    } catch (error) {
      console.error('[RECOMMENDATIONS ERROR]:', error);
      set({ isLoadingRecommendations: false, recommendationError: error.message });
      throw error; // Re-throw so caller knows it failed
    }
  },

  // Clear recommendation cache (e.g., when frequency changes significantly)
  clearRecommendationCache: () => {
    set({ recommendationCache: {} });
  },

  // Replace a generic node with a catalog component
  replaceNodeWithCatalog: async (genericNodeId, catalogComponent) => {
    const { nodes, edges, componentLibrary, setNodes, setEdges, initializeCatalogNode } = get();

    const genericNode = nodes.find(n => n.id === genericNodeId);
    if (!genericNode) return false;

    // Find the catalog component in the library
    const catalogDef = Object.values(componentLibrary).find(
      c => c.partNumber === catalogComponent.partNumber && c.isCatalog
    );

    if (!catalogDef) {
      console.error('Catalog component not found in library:', catalogComponent.partNumber);
      return false;
    }

    // Get the base type display name from COMPONENT_LIBRARY (e.g., "Power Amplifier")
    const baseTypeName = COMPONENT_LIBRARY[catalogDef.baseType]?.displayName || catalogDef.baseType;

    // Create a new node ID
    const newNodeId = `${catalogDef.type}_${Date.now()}`;

    // Build the new catalog node preserving position
    let newNode = {
      id: newNodeId,
      type: catalogDef.type,
      position: { ...genericNode.position },
      data: {
        name: catalogDef.partNumber,
        label: catalogDef.displayName,
        parameters: JSON.parse(JSON.stringify(catalogDef.parameters)),
        isCatalog: true,
        id: catalogDef.id,
        partNumber: catalogDef.partNumber,
        manufacturer: catalogDef.manufacturer,
        baseType: catalogDef.baseType,
        baseTypeName: baseTypeName,
        hasFreqData: catalogDef.hasFreqData || false,
        hasS2P: catalogDef.hasS2P || false
      }
    };

    // Initialize catalog component (load freq data, S2P, set data sources)
    newNode = await initializeCatalogNode(newNode);

    // Update edges to point to the new node
    const updatedEdges = edges.map(edge => {
      if (edge.source === genericNodeId) {
        return { ...edge, source: newNodeId };
      }
      if (edge.target === genericNodeId) {
        return { ...edge, target: newNodeId };
      }
      return edge;
    });

    // Remove the old node and add the new one
    const updatedNodes = nodes.filter(n => n.id !== genericNodeId);
    updatedNodes.push(newNode);

    setNodes(updatedNodes);
    setEdges(updatedEdges);

    return true;
  }
}),
    // Zundo temporal middleware options
    {
      limit: 50,
      // Only track document state (nodes, edges, frequency) - not UI state
      partialize: (state) => ({
        nodes: state.nodes,
        edges: state.edges,
        frequencyMode: state.frequencyMode,
        frequencySingle: state.frequencySingle,
        frequencyStart: state.frequencyStart,
        frequencyStop: state.frequencyStop,
        frequencyStep: state.frequencyStep,
      }),
      // Skip saving when partialized state hasn't changed (fixes history spam)
      equality: (pastState, currentState) =>
        JSON.stringify(pastState) === JSON.stringify(currentState),
      // Deep clone before storing to prevent mutation corruption
      handleSet: (handleSet) => (state) => {
        const clonedState = JSON.parse(JSON.stringify(state));
        handleSet(clonedState);
      },
    }
  )
);

// Export temporal store for undo/redo
export const temporalStore = useStore.temporal;

// Debug: expose on window
if (typeof window !== 'undefined') {
  window._store = useStore;
  window._temporalStore = useStore.temporal;
}

export default useStore;
